import { Injectable } from '@angular/core';
import { ApiService } from 'app/services/api.service';
import { AppState } from 'app/store/states/app.state';
import { Store } from '@ngrx/store';
import { NotificationsService } from 'angular2-notifications';
import { ListingTimelineable } from 'app/models/listing-timeline';
import { Observable } from 'rxjs';
import { omitBy, isEmpty, clone, isNull} from 'lodash-es';

import Pin from 'app/models/pin';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { DashNotificationsService } from 'app/services/dash-notifications.service';

export interface ListingOptions {
  min_bedrooms?: string;
  min_full_bathrooms?: string;
  min_half_bathrooms?: string;
  min_sqft?: string;
  min_price?: string;
  max_price?: string;
  statuses?: string[];
  states?: string[];
  counties?: string[];
  towns?: string[];
  neighborhoods?: string[];
  page_index?: number;
  page_size?: number;
  sort_field?: string;
  sort_direction?: string;
}

export interface ListingLocations {
  [state: string]: {
    [county: string]: {
      [city: string]: string[]
    }
  }
}

export interface ListingsResponse {
  total_listings: number,
  total_pins: Pin[],
  listings: ListingTimelineable[],
  locations: ListingLocations
}

@Injectable({
  providedIn: 'root'
})
export class ListingsService {

  constructor(
    private api: ApiService,
    private router: Router,
    private notifications: DashNotificationsService
  ) {}

  getListings(listingOptions: ListingOptions = {}): Observable<ListingsResponse> {
    let params = this.buildParams(listingOptions);
    return this.api.get<ListingsResponse>('timeline_listings', { params })
      .pipe(
        tap(
          () => {},
          (error: any) => this.notifications.error((error && error.message) ? error.message : 'An error occurred while retrieving the listings.')
        )
      )
  }

  getListing(timelineId: number): Observable<ListingTimelineable>{
    return this.api.get<ListingTimelineable>(`timeline_listings/${timelineId}`)
      .pipe(
        tap(
          () => {},
          (error: any) => {
            if(error.houwzer_error_code === 1003){
              this.notifications.error('It looks like that listing is no longer available. Please contact support@newfoundgroup.com if you have any questions.')
              this.router.navigate(['/our-homes']);
            } else {
              this.notifications.error(error.message ? error.message : 'An error occurred while retrieving the listing.');
            }
          }
        )
      )
  }

  getListingLocations(): Observable<ListingLocations> {
    return this.api.get<ListingLocations>('timeline_listings/locations')
      .pipe(
        tap(
          () => { },
          (error: any) => this.notifications.error(error.message ? error.message : 'An error occurred while retrieving the listing locations.')
        )
      )
  }

  getPins(listingOptions: ListingOptions = {}){

    let params = this.buildParams(listingOptions)
    return this.api.get<Pin[]>('timeline_listings/pins', { params })
      .pipe(
        tap(
          () => { },
          (error: any) => this.notifications.error(error.message ? error.message : 'An error occurred while retrieving the listings.')
        )
      )
  }

  private buildParams(listingOptions: ListingOptions){
    let params: any = {
      min_bedrooms: listingOptions.min_bedrooms,
      min_full_bathrooms: listingOptions.min_full_bathrooms,
      min_half_bathrooms: listingOptions.min_half_bathrooms,
      min_sqft: listingOptions.min_sqft,
      min_price: listingOptions.min_price,
      max_price: listingOptions.max_price,
      'statuses[]': listingOptions.statuses,
      'states[]': listingOptions.states,
      'counties[]': listingOptions.counties,
      'towns[]': listingOptions.towns,
      'neighborhoods[]': listingOptions.neighborhoods,
      sort_field: listingOptions.sort_field,
      sort_direction: listingOptions.sort_direction,
    }

    params = omitBy(params, (value, _) => isEmpty(value) || isNull(value))

    params.page_index = listingOptions.page_index;
    params.page_size = listingOptions.page_size;

    return params
  }
}
